import {
  Alert,
  Box,
  Card,
  Dialog,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { LoadingButton } from '@mui/lab';
import { FormProvider, RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import useLocales from 'src/hooks/useLocales';
import { useStore } from 'src/stores/store';
import { IContractInfo } from 'src/@types/Choqorak/MainTables/Contract/ContractInfo';
import { useEffect, useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import LocalizDatePicker from 'src/sections/common/LocalizDatePicker';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Label from 'src/components/Label';
import { DateConverter } from 'src/sections/common/DateConverter';
import CalculateDuration from 'src/sections/common/CalculateDuration';
import { GetCurrencySymbol } from 'src/sections/common/GetCurrencySymbol';
interface Props {
  id: number;
}

export default observer(function ContractInfoContractInfoExtend({ id }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { ContractInfoStore, ShopStore } = useStore();
  const { selectedContractInfoDetail } = ContractInfoStore;
  const { loadShopDetail, selectedShopDetail, clearSelectedShop } = ShopStore;
  const [durationOfContract, setDurationOfContract] = useState<any>();
  const [checked, setChecked] = useState(false);
  const validationSchema = Yup.object().shape({
    contractStartDate: Yup.string().required(`${translate('Validation.contractStartDate')}`),
    contractEndDate: Yup.string().required(`${translate('Validation.contractEndDate')}`),
  });
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };
  const defaultValues = useMemo<IContractInfo>(() => {
    const oldEndDate = selectedContractInfoDetail?.contractEndDate
      ? new Date(selectedContractInfoDetail.contractEndDate)
      : new Date();

    // Add 1 day to the old end date
    const newStartDate = new Date(oldEndDate);
    newStartDate.setDate(newStartDate.getDate() + 1);

    return {
      contractId: selectedContractInfoDetail?.contractId,
      finalRentPerMonth: selectedContractInfoDetail?.initialRentAmount || undefined,
      contractStartDate: newStartDate,
      contractEndDate: new Date(),
      discountStartDate: undefined,
      discountEndDate: undefined,
      contractDuration: undefined,
    };
  }, [selectedContractInfoDetail]);
  const methods = useForm<any>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    watch,
    setValue,
    control,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const val = watch();
  const onSubmit = (data: IContractInfo) => {
    data.contractStartDate = new Date(data.contractStartDate!);
    data.contractEndDate = new Date(data.contractEndDate!);
    if (data.discountStartDate && data.discountEndDate) {
      data.discountStartDate = new Date(data.discountStartDate!);
      data.discountEndDate = new Date(data.discountEndDate!);
    }

    ContractInfoStore.ContractExtend(data)
      .then(() => {
        ContractInfoStore.setOpenCloseExtendDialog();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
      })
      .catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
      });
  };

  const initialRentAmount = watch('initialRentAmount'); // Watch the initial rent value
  const ContractInfoExtendAmount = watch('ContractInfoExtendAmount'); // Watch the ContractInfoExtend value

  useEffect(() => {
    // Parse values to numbers and calculate final rent
    const initialRent = parseFloat(initialRentAmount) || 0;
    const ContractInfoExtend = parseFloat(ContractInfoExtendAmount) || 0;
    const finalRent = initialRent - ContractInfoExtend;

    // Update the final rent field
    setValue('finalRentPerMonth', finalRent >= 0 ? finalRent : 0); // Ensure no negative values
  }, [initialRentAmount, ContractInfoExtendAmount, setValue]);

  const contractStartDate = watch('contractStartDate');
  const contractEndDate = watch('contractEndDate');
  const contractDisCountStartDate = watch('discountStartDate');
  const contractDisCountEndDate = watch('discountEndDate');
  const finalRentPerMonth = watch('finalRentPerMonth');
  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  useEffect(() => {
    setValue(
      'contractDuration',
      CalculateDuration(translate, contractStartDate, contractEndDate, true)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractStartDate, contractEndDate]);

  const getUserDataFromLocalStorage: any = localStorage.getItem('userData');

  const changeLocalStorgeStringToValue = JSON.parse(getUserDataFromLocalStorage);

  const is_super_admin = changeLocalStorgeStringToValue.IsSuperAdmin;
  useEffect(() => {

  }, [is_super_admin])
  interface DurationType {
    type: number;
    value: number;
  }
  const Duration: DurationType[] = [
    { type: 1, value: 1 },
    { type: 2, value: 2 },
    { type: 3, value: 3 },
    { type: 4, value: 4 },
    { type: 5, value: 5 },
    { type: 6, value: 6 },
    { type: 7, value: 7 },
    { type: 8, value: 8 },
    { type: 9, value: 9 },
    { type: 10, value: 10 },
    { type: 11, value: 11 },
    { type: 12, value: 12 },
    { type: 13, value: 13 },
    { type: 14, value: 14 },
    { type: 15, value: 15 },
  ];

  useEffect(() => {
    if (durationOfContract !== undefined) {
      setValue(
        'contractEndDate',
        new Date(
          new Date(val.contractStartDate!).setFullYear(
            new Date(val.contractStartDate!).getFullYear() + Number(durationOfContract)
          )
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationOfContract]);

  //  useEffect(() => {
  //     if (selectedContractInfoDetail!.shopId) {
  //       loadShopDetail(selectedContractInfoDetail?.shopId).then(()=>{
  //         setValue('finalRentPerMonth', selectedShopDetail!.rentPerMonth);
  //       });
  //     }
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  //   }, [selectedContractInfoDetail!.shopId]);
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* First column for the form */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                ml: 4,
                mt: 4,
                mb: 4,
              }}
            >
              <Stack spacing={2}>
                <LocalizDatePicker
                  name="contractStartDate"
                  label={translate('contractsInfo.StartDate')}
                  control={control}
                  showAsterisk={false}
                  disabled={is_super_admin ? false : true}
                />

                <RHFSelect
                  name="duration"
                  onChange={(e) => {
                    setValue('duration', Number(e.target.value));
                    setDurationOfContract(e.target.value);
                  }}
                  value={watch('duration')}
                  label={translate('contractsInfo.SetContractDuration')}
                >
                  <option value="" />
                  {Duration.map((op, index) => (
                    <option key={index} value={op.type}>
                      {op.value}
                    </option>
                  ))}
                </RHFSelect>

                <LocalizDatePicker
                  name="contractEndDate"
                  label={translate('contractsInfo.EndDate')}
                  control={control}
                  showAsterisk={false}
                />
                <RHFTextField
                  name="contractDuration"
                  label={translate('contractsInfo.ContractDuration')}
                  showAsterisk={true}
                  sx={{ direction: 'rtl' }}
                  InputProps={{
                    readOnly: true,
                  }}
                />

                <RHFTextField
                  name="finalRentPerMonth"
                  label={translate('contractsInfo.FinalRentPerMonth')}
                  size="small"
                />

                <Stack spacing={2} sx={{ mb: 2 }}>
                  <>
                    {/* Hidden File Input */}
                    <input
                      type="file"
                      // accept=".jpg"
                      id="contractattachment"
                      onChange={(e) => {
                        const file = e.target.files ? e.target.files[0] : null;
                        setValue('attachment', file);
                      }}
                      style={{ display: 'none' }}
                    />

                    {/* TextField with Attachment Icon */}
                    <TextField
                      label="Attach File"
                      name="attachment"
                      value={watch('attachment')?.name || ''} // Display file name
                      placeholder="No file chosen"
                      InputProps={{
                        readOnly: true, // Prevent manual input
                        startAdornment: (
                          <InputAdornment position="start">
                            {/* Attachment Icon */}
                            <label htmlFor="contractattachment">
                              <IconButton component="span">
                                <AttachFileIcon />
                              </IconButton>
                            </label>
                          </InputAdornment>
                        ),
                      }}
                      fullWidth
                      size="medium"
                      sx={{ m: 1, width: '100%' }}
                    />
                  </>
                </Stack>

                {!checked ? (
                  <Controller
                    name="hasDiscount"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            onChange={(e) => {
                              field.onChange(e); // Notify React Hook Form of the value change
                              handleChange(e); // Handle the switch state change
                            }}
                            checked={field.value}
                          />
                        }
                        label={translate('HasDiscount')}
                        labelPlacement="end"
                      />
                    )}
                  />
                ) : (
                  ''
                )}
                {checked ? (
                  <>
                    <LocalizDatePicker
                      name="discountStartDate"
                      label={translate('contractsInfo.DiscountStartDate')}
                      control={control}
                      showAsterisk={false}
                    />

                    <LocalizDatePicker
                      name="discountEndDate"
                      label={translate('contractsInfo.DiscountEndDate')}
                      control={control}
                      showAsterisk={false}
                    />
                  </>
                ) : (
                  ''
                )}
              </Stack>
            </Box>
            <Box
              sx={{
                ml: 4,
                mt: 4,
                mb: 4,
              }}
            >
              <Stack direction="row" spacing={3}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  color="success"
                  loading={isSubmitting}
                  startIcon={<Iconify icon="eva:edit-fill" />}
                >
                  {translate('CRUD.Save')}
                </LoadingButton>

                <LoadingButton
                  variant="contained"
                  size="small"
                  onClick={() => ContractInfoStore.setOpenCloseExtendDialog()}
                  startIcon={<CancelIcon />}
                >
                  {translate('CRUD.Cancle')}
                </LoadingButton>
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* Second column for displaying year and month information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* اطلاعات قرارداد قبلی */}
              <Label color="warning">
                <Typography variant="h6" align="center">
                  {translate('contractsInfo.LastContractInfo')}
                </Typography>
              </Label>

              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>{translate('Customer.CustomerName')}</TableCell>
                    <TableCell>{selectedContractInfoDetail?.customerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('Shop.ShopCode')}</TableCell>
                    <TableCell>{selectedContractInfoDetail?.shopCode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.StartDate')}</TableCell>
                    <TableCell>
                      <DateConverter date={selectedContractInfoDetail?.contractStartDate} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.EndDate')}</TableCell>
                    <TableCell>
                      <DateConverter date={selectedContractInfoDetail?.contractEndDate} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.ContractDuration')}</TableCell>
                    <TableCell>
                      {CalculateDuration(
                        translate,
                        selectedContractInfoDetail?.contractStartDate,
                        selectedContractInfoDetail?.contractEndDate
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.DurationOfDiscount')}</TableCell>
                    <TableCell>
                      {CalculateDuration(
                        translate,
                        selectedContractInfoDetail?.discountStartDate,
                        selectedContractInfoDetail?.discountEndDate
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.FinalRentPerMonth')}</TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.finalRentPerMonth}{' '}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.TotalContractDurationRent')}</TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.totalContractDurationRent}{' '}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {translate('contractsInfo.TotalContractDurationPaidRent')}
                    </TableCell>
                    <TableCell>
                      {selectedContractInfoDetail?.totalContractDurationPaidRent}{' '}
                      {selectedContractInfoDetail?.rentCarrencyId === 2
                        ? GetCurrencySymbol('afn')
                        : GetCurrencySymbol('dollar')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider />

              {/* اطلاعات قرارداد جدید */}
              <Label color="success">
                <Typography variant="h6" align="center">
                  {translate('contractsInfo.NewContractInfo')}
                </Typography>
              </Label>

              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.ContractDuration')}</TableCell>
                    <TableCell>
                      {CalculateDuration(translate, contractStartDate, contractEndDate)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.DurationOfDiscount')}</TableCell>
                    <TableCell>
                      {CalculateDuration(
                        translate,
                        contractDisCountStartDate,
                        contractDisCountEndDate
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{translate('contractsInfo.FinalRentPerMonth')}</TableCell>
                    <TableCell>{finalRentPerMonth}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
